import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, LifeBuoy, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const HelpPage: React.FC = () => {
  const { t } = useTranslation();
  const supportEmail = 'solvion.ai@gmail.com';
  const supportWhatsapp = '5564996474358';
  const whatsappLink = `https://wa.me/${supportWhatsapp}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8"
    >
      <motion.h1
        className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary flex items-center gap-3"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        <LifeBuoy className="size-8" /> {t('help.title')}
      </motion.h1>

      <p className="text-lg text-gray-600 dark:text-gray-400">
        {t('help.subtitle')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Card de E-mail */}
        <motion.div
          className="card p-6 flex flex-col gap-4 hover-lift"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Mail className="size-8 text-primary" />
          <h3 className="text-xl font-bold text-[#181411] dark:text-white">{t('help.email_support_title')}</h3>
          <p className="text-gray-600 dark:text-gray-400">{t('help.email_support_description')}</p>
          <a
            href={`mailto:${supportEmail}`}
            className="mt-2 text-primary font-semibold hover:underline flex items-center gap-1"
          >
            {supportEmail}
          </a>
        </motion.div>

        {/* Card de WhatsApp */}
        <motion.div
          className="card p-6 flex flex-col gap-4 hover-lift"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
        >
          <MessageSquare className="size-8 text-green-500" />
          <h3 className="text-xl font-bold text-[#181411] dark:text-white">{t('help.whatsapp_support_title')}</h3>
          <p className="text-gray-600 dark:text-gray-400">{t('help.whatsapp_support_description')}</p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 text-green-500 font-semibold hover:underline flex items-center gap-1"
          >
            {supportWhatsapp}
          </a>
        </motion.div>

        {/* Disclaimer Card - Full Width */}
        <motion.div
          className="card p-6 flex gap-3 items-start bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 md:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 300, damping: 20 }}
        >
          <AlertTriangle className="size-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100 mb-2">
              {t('disclaimers.whatsapp_blocking_title')}
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
              {t('disclaimers.whatsapp_blocking_message')}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default HelpPage;